import type { ColDef } from "ag-grid-community";
import React from "react";

import { Badge } from "@/components/ui/badge";
import { Heading } from "@/components/ui/heading";
import { Link } from "@/components/ui/link";
import { SearchField, SearchInput } from "@/components/ui/search-field";
import { Text } from "@/components/ui/text";
import type {
  BillingInvoiceDetail,
  BillingInvoiceLineItem,
} from "@/features/admin/types";
import {
  accessionColumn,
  alternateIdColumn,
  descriptionColumn,
  donorIdColumn,
  orgColumn,
  priceColumn,
  unosIdColumn,
} from "@/features/admin/utils/columns";
import { formatCurrencyValue } from "@/features/admin/utils/currency";
import { useAdminGridOptions } from "@/features/admin/utils/gridState";
import { AdminGrid, denseAdminGridTheme } from "../AdminGrid";

export function InvoicePreview({
  invoice,
  isPreview = false,
}: {
  invoice: BillingInvoiceDetail | undefined;
  isPreview?: boolean;
}) {
  const [search, setSearch] = React.useState("");

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden rounded-lg border bg-background shadow-xs">
      <div className="flex flex-col gap-6 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2">
            <Text
              muted
              size="xs"
              className="font-medium uppercase tracking-wide"
            >
              Bill to
            </Text>
            {isPreview && (
              <Badge variant="soft" color="amber" size="sm">
                Pending - Preview
              </Badge>
            )}
          </div>
          <Heading
            level={2}
            displayLevel={3}
            className="truncate font-semibold"
          >
            {invoice?.org.name ?? "-"}
          </Heading>
        </div>
        <dl className="grid grid-cols-[auto_auto] items-baseline gap-x-6 gap-y-2 sm:justify-end">
          {!isPreview && (
            <>
              <Text elementType="dt" muted size="sm">
                Invoice number
              </Text>
              <Text
                elementType="dd"
                size="sm"
                className="text-right tabular-nums"
              >
                {invoice?.id ?? "-"}
              </Text>
            </>
          )}
          <Text elementType="dt" muted size="sm">
            Invoice date
          </Text>
          <Text elementType="dd" size="sm" className="text-right">
            {invoice?.createdAt.toLocaleDateString() ?? "-"}
          </Text>
          <Text elementType="dt" muted size="sm">
            Amount due
          </Text>
          <Text
            elementType="dd"
            size="md"
            bold
            className="text-right tabular-nums"
          >
            {typeof invoice?.total === "number"
              ? formatCurrencyValue(invoice.total)
              : "-"}
          </Text>
        </dl>
      </div>
      <div className="flex items-center justify-between gap-3 border-t bg-muted/30 px-6 py-3">
        <Text size="sm" bold>
          Line items
        </Text>
        <SearchField
          value={search}
          onChange={setSearch}
          aria-label="Search line items"
          className="w-64"
        >
          <SearchInput placeholder="Search line items..." />
        </SearchField>
      </div>
      <div className="flex flex-1 min-h-0 flex-col border-t">
        <LineItemsTable
          lineItems={invoice?.lineItems ?? []}
          sampleFieldColumns={invoice?.sampleFieldColumns ?? []}
          quickFilterText={search}
        />
      </div>
    </div>
  );
}

function LineItemsTable({
  lineItems,
  sampleFieldColumns,
  quickFilterText,
}: {
  lineItems: BillingInvoiceLineItem[];
  sampleFieldColumns: BillingInvoiceDetail["sampleFieldColumns"];
  quickFilterText: string;
}) {
  const columnDefs = React.useMemo<ColDef<BillingInvoiceLineItem>[]>(
    () => [
      {
        ...accessionColumn,
        colId: "accessionId",
        headerName: "Accession",
        valueGetter: ({ data }) => data?.accessionId ?? null,
        cellRenderer: ({ data }: { data?: BillingInvoiceLineItem }) =>
          data ? (
            <Link
              href={{
                to: "/accessions/$id",
                params: { id: data.accessionId },
              }}
            >
              {data.accessionId}
            </Link>
          ) : null,
      },
      {
        ...donorIdColumn,
        colId: "donorId",
        headerName: "Donor ID",
        valueGetter: ({ data }) => data?.caseId ?? "",
        cellRenderer: ({ data }: { data?: BillingInvoiceLineItem }) =>
          data ? (
            <Link
              href={{
                to: "/subjects/$id/accession",
                params: { id: data.accession.subject.id },
                search: { accessionId: data.accessionId },
              }}
            >
              {data.caseId}
            </Link>
          ) : null,
      },
      {
        ...unosIdColumn,
        colId: "unosId",
        headerName: "UNOS ID",
        valueGetter: ({ data }) => data?.unosId ?? "",
      },
      {
        ...alternateIdColumn,
        colId: "alternateId",
        headerName: "Alternate ID",
        valueGetter: ({ data }) => data?.donorIds?.join(", ") ?? "",
      },
      {
        ...descriptionColumn,
        field: "description",
        headerName: "Description",
      },
      ...sampleFieldColumns.map<ColDef<BillingInvoiceLineItem>>(
        (column, index) => ({
          colId: `sampleField-${column.fieldSpecId}`,
          headerName: column.header,
          minWidth: 120,
          valueGetter: ({ data }) => data?.sampleFieldValues[index] ?? "",
        }),
      ),
      {
        ...orgColumn,
        colId: "splitWith",
        headerName: "Split With",
        flex: undefined,
        minWidth: 140,
        valueGetter: ({ data }) => {
          if (!data) return "";
          if (data.accession.splitBillings.length > 0) {
            return data.accession.splitBillings
              .map((billing) => billing.org.name)
              .join(", ");
          }
          return data.splitOrg?.name ?? "-";
        },
      },
      {
        ...priceColumn,
        colId: "total",
        headerName: "Total",
        maxWidth: 140,
        valueGetter: ({ data }) => data?.price.toNumber() ?? 0,
      },
    ],
    [sampleFieldColumns],
  );

  const { options } = useAdminGridOptions<BillingInvoiceLineItem>(
    lineItems,
    columnDefs,
    {
      getRowId: ({ data }) => String(data.instructionId),
      quickFilterText,
      defaultColDef: {
        flex: 1,
        resizable: true,
        sortable: false,
        filterParams: { maxNumConditions: 1 },
      },
      suppressDragLeaveHidesColumns: true,
    },
  );

  return (
    <AdminGrid<BillingInvoiceLineItem>
      {...options}
      theme={denseAdminGridTheme}
    />
  );
}
